import { Volunteer, VolunteerFiltersProps } from "../data/types";

const AGE_AT_DATE = new Date('2026-02-01');

// Using provided age because we currently care about the age at a specific date rather than current age
// TODO Change this for future use cases
export function calculateAge(volunteer?: Volunteer) {
    // First, check if age is directly provided
    if (volunteer?.age !== null && volunteer?.age !== undefined) {
        return volunteer.age;
    }

    // Next, try to calculate age from date of birth
    if (volunteer?.dob) {
        const dob = new Date(volunteer.dob);
        if (!isNaN(dob.getTime())) {
            const today = AGE_AT_DATE;
            let years = today.getFullYear() - dob.getFullYear();

            if (
                today.getMonth() < dob.getMonth() ||
                (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
            ) {
                years--;
            }

            if (years >= 0) return years;
        }
    }

    return 'N/A';
};

export function getFilteredVolunteers<T extends Volunteer>(allVolunteers: T[], filters: VolunteerFiltersProps): T[] {
    let data = allVolunteers

    let name = filters.name.toLowerCase().trim()
    // Text filters
    if (filters.name) {
        data = data.filter(v => `${v.first_name} ${v.last_name}`.trim().toLowerCase().includes(name))
    }

    // ID filter
    if (filters.id) {
        data = data.filter(v => String(v.csid) === filters.id)
    }

    // if (filters.country) {
    //     data = data.filter(v =>
    //         v.country?.toLowerCase().includes(filters.country.toLowerCase())
    //     )
    // }

    let country_of_birth = filters.country_of_birth.toLowerCase().trim()
    if (filters.country_of_birth) {
        data = data.filter(v => v.country_of_birth?.toLowerCase().includes(country_of_birth))
    }

    let jamat_khana = filters.jamat_khana.toLowerCase().trim()
    if (filters.jamat_khana) {
        data = data.filter(v => v.jamat_khana?.toLowerCase().includes(jamat_khana))
    }

    let family_origin = filters.family_origin.toLowerCase().trim()
    if (filters.family_origin) {
        data = data.filter(v => v.family_origin?.toLowerCase().includes(family_origin))
    }

    let email = filters.email?.toLowerCase().trim()
    if (filters.email) {
        data = data.filter(v => v.email?.toLowerCase().includes(email))
    }

    let phone_number = filters.phone_number?.replace(/\s/g, '').toLowerCase()
    if (filters.phone_number) {
        data = data.filter(v => v.phone?.toLowerCase().includes(phone_number))
    }

    // Age Range Filtering
    if (filters.age_start !== null) {
        data = data.filter(v => {
            const age = v.age ?? calculateAge(v);
            return age !== 'N/A' && Number(age) >= filters.age_start;
        });
    }

    if (filters.age_end !== null) {
        data = data.filter(v => {
            const age = v.age ?? calculateAge(v);
            return age !== 'N/A' && Number(age) <= filters.age_end;
        });
    }

    if (filters.volunteer_for_visit !== null) {
        data = data.filter(v => v.volunteer_for_visit == filters.volunteer_for_visit)
    }

    // Country filter (Local vs International)
    if (filters.country && filters.country !== 'both') {
        if (filters.country === 'uk_ireland') {
            data = data.filter(v => {
                const country = v.country?.toLowerCase() || '';
                return country.includes('austria') ||
                       country.includes('cyprus') ||
                       country.includes('denmark') ||
                       country.includes('estonia') ||
                       country.includes('finland') ||
                       country.includes('germany') ||
                       country.includes('hungary') ||
                       country.includes('italy') ||
                       country.includes('netherlands') ||
                       country.includes('north macedonia') ||
                       country.includes('norway') ||
                       country.includes('poland') ||
                       country.includes('sweden') ||
                       country.includes('slovenia') ||
                       country.includes('lithuania') ||
                       country.includes('greece');
            });
        } else if (filters.country === 'international') {
            data = data.filter(v => {
                const country = v.country?.toLowerCase() || '';
                return !country.includes('austria') &&
                       !country.includes('cyprus') &&
                       !country.includes('denmark') &&
                       !country.includes('estonia') &&
                       !country.includes('finland') &&
                       !country.includes('germany') &&
                       !country.includes('hungary') &&
                       !country.includes('italy') &&
                       !country.includes('netherlands') &&
                       !country.includes('north macedonia') &&
                       !country.includes('norway') &&
                       !country.includes('poland') &&
                       !country.includes('sweden') &&
                       !country.includes('slovenia') &&
                       !country.includes('lithuania') &&
                       !country.includes('greece');
            });
        }
    }

    // IIUK registration filter
    if (filters.iiuk_registeration && filters.iiuk_registeration !== 'any') {
        if (filters.iiuk_registeration === 'iiuk') {
            // IIUK registered: true, 1, or null (treated as IIUK by default)
            data = data.filter(v => {
                const iiuk = v.iiuk_registeration;
                return iiuk === true || iiuk === 1 || iiuk === null || iiuk === undefined;
            });
        } else if (filters.iiuk_registeration === 'non_iiuk') {
            // Non-IIUK: explicitly false or 0
            data = data.filter(v => {
                const iiuk = v.iiuk_registeration;
                return iiuk === false || iiuk === 0;
            });
        }
    }

    // Boolean filters
    if (filters.ismaili !== null) {
        data = data.filter(v => v.ismaili === filters.ismaili)
    }

    if (filters.eligible_for_duty !== null) {

        data = data.filter(v => v.eligible_for_duty === filters.eligible_for_duty)

    }

    if (filters.multi_faith_family !== null) {

        data = data.filter(v => v.multi_faith_family === filters.multi_faith_family)

    }

    if (filters.has_support_needs !== null) {
        data = data.filter(v => {
            const hasSupport = !!(v.support_needs?.trim())
            return hasSupport === filters.has_support_needs
        })
    }


    if (filters.registered_volunteer !== null) {

        data = data.filter(v => v.registered_volunteer === filters.registered_volunteer)
    }

    if (filters.profile_submitted !== null) {
        data = data.filter(v => Boolean(v.profile_submitted_at) === filters.profile_submitted)
    }

    if (filters.approved_volunteer !== null) {
        data = data.filter(v => {
            const approvedVolunteer = v.approved_volunteer as boolean | number | null | undefined
            return filters.approved_volunteer
                ? approvedVolunteer === true || approvedVolunteer === 1
                : approvedVolunteer === false || approvedVolunteer === 0
        })
    }

    // Array filters
    if (filters.tags.length > 0) {
        data = data.filter(v =>
            filters.tags.every(tag =>
                v.flags.some(f => f.id === tag.id)
            )
        );
    }


    if (filters.preferences.length > 0) {
        data = data.filter(v =>
            filters.preferences.every(pref =>
                v.preferences.some(p => p.id === pref.id)
            )
        );
    }


    if (filters.trainings.length > 0) {
        const trainingFilterType = filters.training_filter_type || 'at_least_one';

        switch (trainingFilterType) {
            case 'all':
                // Volunteer must have completed ALL selected trainings
                data = data.filter(v =>
                    filters.trainings.every(training =>
                        v.trainings.some(t =>
                            t.id === training.id &&
                            Number(t.pivot?.completion_status) === 1
                        )
                    )
                );
                break;

            case 'not_all':
                // Volunteer is missing at least one of the selected trainings (opposite of 'all')
                data = data.filter(v =>
                    !filters.trainings.every(training =>
                        v.trainings.some(t =>
                            t.id === training.id &&
                            Number(t.pivot?.completion_status) === 1
                        )
                    )
                );
                break;

            case 'none':
                // Volunteer must NOT have completed ANY of the selected trainings
                data = data.filter(v =>
                    !filters.trainings.some(training =>
                        v.trainings.some(t =>
                            t.id === training.id &&
                            Number(t.pivot?.completion_status) === 1
                        )
                    )
                );
                break;

            case 'at_least_one':
            default:
                // Volunteer must have completed AT LEAST ONE of the selected trainings
                data = data.filter(v =>
                    filters.trainings.some(training =>
                        v.trainings.some(t =>
                            t.id === training.id &&
                            Number(t.pivot?.completion_status) === 1
                        )
                    )
                );
                break;
        }
    }


    if (filters.languages.length > 0) {
        data = data.filter(v => {
            const langs = v.languages
                ?.split(', ')
                .map(l => l.trim().toLowerCase()) || [];

            return filters.languages.some(l =>
                langs.includes(l.toLowerCase())
            );
        });
    }


    if (filters.service_record.length > 0) {
        data = data.filter(v =>
            filters.service_record.includes(v.service_record)
        );
    }

    if (filters.genders.length > 0) {
        data = data.filter(v =>
            filters.genders
                .map(g => g.toLowerCase())
                .includes(v.gender?.toLowerCase())
        );
    }

    if (filters.availability.length > 0) {
        const availabilityFilterType = filters.availability_filter_type || 'at_least_one';

        data = data.filter(v => {
            if (!v.availability || !Array.isArray(v.availability)) {
                return availabilityFilterType === 'none';
            }

            switch (availabilityFilterType) {
                case 'all':
                    return filters.availability.every(filterAvail =>
                        v.availability.includes(filterAvail)
                    );
                case 'none':
                    return !filters.availability.some(filterAvail =>
                        v.availability.includes(filterAvail)
                    );
                case 'at_least_one':
                default:
                    return filters.availability.some(filterAvail =>
                        v.availability.includes(filterAvail)
                    );
            }
        });
    }

    // Event assignment filter
    // Note: This is complex and typically handled by backend
    // Frontend can only do basic filtering on allocations if data is available
    if (filters.event_assignment_type && filters.event_assignment_events.length > 0) {
        const eventIds = filters.event_assignment_events.map(e => e.id);

        if (filters.event_assignment_type === 'assigned') {
            // Show volunteers assigned to any of the selected events
            data = data.filter(v => {
                // Check if volunteer has allocations in these events
                const hasAllocation = v.allocations?.some(alloc =>
                    alloc.shift?.area?.event_id && eventIds.includes(alloc.shift.area.event_id)
                ) || false;
                return hasAllocation;
            });
        } else if (filters.event_assignment_type === 'not_assigned') {
            // Show volunteers NOT assigned to any of the selected events
            data = data.filter(v => {
                const hasAllocation = v.allocations?.some(alloc =>
                    alloc.shift?.area?.event_id && eventIds.includes(alloc.shift.area.event_id)
                ) || false;
                return !hasAllocation;
            });
        }
    }

    // Core team assignment filter
    if (filters.core_team_assignment_type) {
        if (filters.core_team_assignment_type === 'has_core_team') {
            // Show volunteers who have at least one core team assignment
            data = data.filter(v => v.coreTeams && v.coreTeams.length > 0);
        } else if (filters.core_team_assignment_type === 'no_core_team') {
            // Show volunteers who have no core team assignments
            data = data.filter(v => !v.coreTeams || v.coreTeams.length === 0);
        }
    }

    return data;
}


export const generateCSID = (): string => {
    return Date.now().toString();
};


export const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    const lowerCaseStr = str.toLowerCase();
    return lowerCaseStr.charAt(0).toUpperCase() + lowerCaseStr.slice(1);
};
